import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Cpu, TrendingUp } from "lucide-react";

const PHASES = [
  {
    n: "01",
    icon: GraduationCap,
    kicker: "The Institute",
    title: "Training & Strategy",
    body: "We audit your workflow, train your managers on operational efficiency, and identify where you are quietly leaking money.",
    tag: "LIFE Foundation",
  },
  {
    n: "02",
    icon: Cpu,
    kicker: "The Engine",
    title: "Empowerment Infrastructure",
    body: "We deploy your custom NEX Engine — automating inventory, tracking, scheduling and communication bottlenecks in weeks, not quarters.",
    tag: "NEX Deploy",
  },
  {
    n: "03",
    icon: TrendingUp,
    kicker: "The LLC Core",
    title: "Business Development",
    body: "We use live data from your NEX Engine to help you scale revenue, defend margin, and make the next hire, purchase, or pivot with proof.",
    tag: "Scale · Retain",
  },
];

const EmpowermentTimeline = () => {
  return (
    <section id="program" data-testid="program-section" className="relative py-28 lg:py-36 bg-[#0A0F1C]">
      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32">
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-8 bg-nex-gold" />
            <span className="mono-label">// The Three-Phase Empowerment Program</span>
          </div>
          <h2 className="serif-head text-4xl md:text-6xl mb-6">
            One framework. <span className="italic text-nex-gold">Three phases.</span> Every client.
          </h2>
          <p className="text-nex-chrome text-lg leading-relaxed">
            NEX doesn't sell software. LIFE doesn't sell consulting. Together they deliver a single, elite empowerment program that stacks training, infrastructure, and revenue development into one thread of accountability.
          </p>
        </div>

        <div className="relative">
          {/* connecting line */}
          <div className="hidden md:block absolute top-16 left-8 right-8 h-px bg-gradient-to-r from-transparent via-nex-gold/40 to-transparent" />

          <div className="grid md:grid-cols-3 gap-6 lg:gap-10">
            {PHASES.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.n}
                  data-testid={`phase-${p.n}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.15, ease: [0.19, 1, 0.22, 1] }}
                  className="relative"
                >
                  {/* number node on the line */}
                  <div className="hidden md:flex absolute -top-2 left-6 w-12 h-12 bg-[#0B0F19] border border-nex-gold text-nex-gold items-center justify-center mono-body text-sm font-bold z-10">
                    {p.n}
                  </div>

                  <div className="card-industrial corner-tick p-7 pt-16">
                    <div className="md:hidden mono-label mb-4">PHASE.{p.n}</div>
                    <Icon size={22} className="text-nex-gold mb-4" />
                    <div className="mono-label mb-2">{p.kicker}</div>
                    <h3 className="serif-head text-2xl mb-4">{p.title}</h3>
                    <p className="text-nex-chrome text-sm leading-relaxed mb-5">{p.body}</p>
                    <div className="metal-divider mb-4" />
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-silver">
                      Delivered by · <span className="text-nex-gold">{p.tag}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmpowermentTimeline;
